import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';

const EventsPageEdit = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    bannerTitle: '',
    bannerSubtitle: '',
    bannerImage: '',
    pageIntroTitle: '',
    pageIntroDescription: '',
    collegeLifeTitle: '',
    collegeLifeSubtitle: '',
    studentsTrip: {
      title: '',
      description: '',
      industrialVisitPhotos: [],
      technicalTourGallery: [],
      studyTourHighlights: []
    },
    treePlantation: {
      title: '',
      description: '',
      images: []
    },
    tabDistribution: {
      title: '',
      description: '',
      images: []
    },
    yogaDay: {
      title: '',
      description: '',
      images: []
    },
    sports: {
      title: '',
      description: '',
      images: []
    },
    ctaSection: {},
    metaTitle: '',
    metaDescription: ''
  });

  useEffect(() => {
    fetchEventsPageContent();
  }, []);

  const fetchEventsPageContent = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEventsPageContent();
      if (response.data) {
        // Ensure all gallery arrays and nested objects exist with proper structure
        const formattedData = {
          bannerTitle: response.data.bannerTitle || '',
          bannerSubtitle: response.data.bannerSubtitle || '',
          bannerImage: response.data.bannerImage || '',
          pageIntroTitle: response.data.pageIntroTitle || '',
          pageIntroDescription: response.data.pageIntroDescription || '',
          collegeLifeTitle: response.data.collegeLifeTitle || '',
          collegeLifeSubtitle: response.data.collegeLifeSubtitle || '',
          studentsTrip: {
            title: response.data.studentsTrip?.title || '',
            description: response.data.studentsTrip?.description || '',
            industrialVisitPhotos: Array.isArray(response.data.studentsTrip?.industrialVisitPhotos) 
              ? response.data.studentsTrip.industrialVisitPhotos 
              : [],
            technicalTourGallery: Array.isArray(response.data.studentsTrip?.technicalTourGallery) 
              ? response.data.studentsTrip.technicalTourGallery 
              : [],
            studyTourHighlights: Array.isArray(response.data.studentsTrip?.studyTourHighlights) 
              ? response.data.studentsTrip.studyTourHighlights 
              : []
          },
          treePlantation: {
            title: response.data.treePlantation?.title || '',
            description: response.data.treePlantation?.description || '',
            images: Array.isArray(response.data.treePlantation?.images) 
              ? response.data.treePlantation.images 
              : []
          },
          tabDistribution: {
            title: response.data.tabDistribution?.title || '',
            description: response.data.tabDistribution?.description || '',
            images: Array.isArray(response.data.tabDistribution?.images) 
              ? response.data.tabDistribution.images 
              : []
          },
          yogaDay: {
            title: response.data.yogaDay?.title || '',
            description: response.data.yogaDay?.description || '',
            images: Array.isArray(response.data.yogaDay?.images) 
              ? response.data.yogaDay.images 
              : []
          },
          sports: {
            title: response.data.sports?.title || '',
            description: response.data.sports?.description || '',
            images: Array.isArray(response.data.sports?.images) 
              ? response.data.sports.images 
              : []
          },
          ctaSection: response.data.ctaSection || {},
          metaTitle: response.data.metaTitle || '',
          metaDescription: response.data.metaDescription || ''
        };
        
        console.log('Loaded form data:', formattedData);
        setFormData(formattedData);
      }
    } catch (error) {
      console.error('Error fetching events page content:', error);
      setError('Failed to load events page content');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleArrayChange = (section, field, index, subfield, value) => {
    setFormData(prev => {
      const newSection = { ...prev[section] };
      if (subfield) {
        newSection[field][index][subfield] = value;
      } else {
        newSection[field][index] = value;
      }
      return {
        ...prev,
        [section]: newSection
      };
    });
  };

  const handleStatChange = (index, field, value) => {
    setFormData(prev => {
      const newStats = [...prev.treePlantation.impact.stats];
      newStats[index][field] = value;
      return {
        ...prev,
        treePlantation: {
          ...prev.treePlantation,
          impact: {
            ...prev.treePlantation.impact,
            stats: newStats
          }
        }
      };
    });
  };

  const handleSportsActivityChange = (index, field, value) => {
    setFormData(prev => {
      const newActivities = [...prev.sports.activities];
      newActivities[index][field] = value;
      return {
        ...prev,
        sports: {
          ...prev.sports,
          activities: newActivities
        }
      };
    });
  };

  const handleListItemChange = (section, subsection, index, value) => {
    setFormData(prev => {
      const newItems = [...prev[section][subsection].items];
      newItems[index] = value;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section][subsection],
            items: newItems
          }
        }
      };
    });
  };

  const addListItem = (section, subsection) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          items: [...prev[section][subsection].items, '']
        }
      }
    }));
  };

  const removeListItem = (section, subsection, index) => {
    setFormData(prev => {
      const newItems = prev[section][subsection].items.filter((_, i) => i !== index);
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section][subsection],
            items: newItems
          }
        }
      };
    });
  };

  // Helper functions for image management
  const addImage = (section, field = 'images') => {
    setFormData(prev => {
      // Create a deep copy to avoid mutations
      const newData = { ...prev };
      
      // Ensure the section exists
      if (!newData[section]) {
        newData[section] = {};
      }
      
      // Ensure the field exists as an array
      if (!newData[section][field]) {
        newData[section][field] = [];
      }
      
      // Create a new array with the existing items plus the new one
      newData[section] = {
        ...newData[section],
        [field]: [
          ...newData[section][field],
          { title: '', url: '', alt: '' }
        ]
      };
      
      console.log(`Adding image to ${section}.${field}`, newData[section][field]);
      return newData;
    });
  };

  const removeImage = (section, field = 'images', index) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].filter((_, i) => i !== index)
      }
    }));
  };

  const updateImage = (section, field = 'images', index, property, value) => {
    setFormData(prev => {
      const newImages = [...prev[section][field]];
      newImages[index] = { ...newImages[index], [property]: value };
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: newImages
        }
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await apiService.updateEventsPageContent(formData);
      setSuccess('Events page content updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving events page content:', error);
      setError('Failed to save events page content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading content...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="mb-8">
        <h1 className="text-3xl font-sans font-bold text-slate mb-2">Events Page Content</h1>
        <p className="text-gray-600">Manage the static content sections on the events page.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Banner Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Banner Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Title</label>
              <input
                type="text"
                value={formData.bannerTitle}
                onChange={(e) => handleInputChange('bannerTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Subtitle</label>
              <input
                type="text"
                value={formData.bannerSubtitle}
                onChange={(e) => handleInputChange('bannerSubtitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Banner Image</label>
              <ImageUpload
                value={formData.bannerImage}
                onChange={(imageUrl) => handleInputChange('bannerImage', imageUrl)}
                placeholder="Banner image filename"
              />
            </div>
          </div>
        </div>

        {/* Page Introduction */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Page Introduction</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Introduction Title</label>
              <input
                type="text"
                value={formData.pageIntroTitle}
                onChange={(e) => handleInputChange('pageIntroTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Introduction Description</label>
              <textarea
                value={formData.pageIntroDescription}
                onChange={(e) => handleInputChange('pageIntroDescription', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* College Life Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">College Life Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Section Title</label>
              <input
                type="text"
                value={formData.collegeLifeTitle}
                onChange={(e) => handleInputChange('collegeLifeTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Section Subtitle</label>
              <textarea
                value={formData.collegeLifeSubtitle}
                onChange={(e) => handleInputChange('collegeLifeSubtitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Students Trip Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Educational Trips & Field Visits</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Section Title</label>
              <input
                type="text"
                value={formData.studentsTrip?.title || ''}
                onChange={(e) => handleNestedChange('studentsTrip', 'title', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Description</label>
              <textarea
                value={formData.studentsTrip?.description || ''}
                onChange={(e) => handleNestedChange('studentsTrip', 'description', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Gallery Images</label>
              <div className="space-y-4">
                <div className="grid gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-slate mb-3">Industrial Visit Photos</h4>
                    {formData.studentsTrip?.industrialVisitPhotos?.map((image, index) => (
                      <div key={index} className="bg-white p-3 rounded border mb-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                          <input
                            type="text"
                            value={image.title || ''}
                            onChange={(e) => updateImage('studentsTrip', 'industrialVisitPhotos', index, 'title', e.target.value)}
                            className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                            placeholder="Image title"
                          />
                          <input
                            type="text"
                            value={image.alt || ''}
                            onChange={(e) => updateImage('studentsTrip', 'industrialVisitPhotos', index, 'alt', e.target.value)}
                            className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                            placeholder="Alt text"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => removeImage('studentsTrip', 'industrialVisitPhotos', index)}
                              className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                        <ImageUpload
                          value={image.url}
                          onChange={(url) => updateImage('studentsTrip', 'industrialVisitPhotos', index, 'url', url)}
                          placeholder="Image filename"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addImage('studentsTrip', 'industrialVisitPhotos')}
                      className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-crimson hover:text-crimson transition-colors"
                    >
                      <i className="fas fa-plus mr-2"></i>
                      Add Industrial Visit Photo
                    </button>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-slate mb-3">Technical Tour Gallery</h4>
                    {formData.studentsTrip?.technicalTourGallery?.map((image, index) => (
                      <div key={index} className="bg-white p-3 rounded border mb-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                          <input
                            type="text"
                            value={image.title || ''}
                            onChange={(e) => updateImage('studentsTrip', 'technicalTourGallery', index, 'title', e.target.value)}
                            className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                            placeholder="Image title"
                          />
                          <input
                            type="text"
                            value={image.alt || ''}
                            onChange={(e) => updateImage('studentsTrip', 'technicalTourGallery', index, 'alt', e.target.value)}
                            className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                            placeholder="Alt text"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => removeImage('studentsTrip', 'technicalTourGallery', index)}
                              className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                        <ImageUpload
                          value={image.url}
                          onChange={(url) => updateImage('studentsTrip', 'technicalTourGallery', index, 'url', url)}
                          placeholder="Image filename"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addImage('studentsTrip', 'technicalTourGallery')}
                      className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-crimson hover:text-crimson transition-colors"
                    >
                      <i className="fas fa-plus mr-2"></i>
                      Add Technical Tour Photo
                    </button>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-slate mb-3">Study Tour Highlights</h4>
                    {formData.studentsTrip?.studyTourHighlights?.map((image, index) => (
                      <div key={index} className="bg-white p-3 rounded border mb-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                          <input
                            type="text"
                            value={image.title || ''}
                            onChange={(e) => updateImage('studentsTrip', 'studyTourHighlights', index, 'title', e.target.value)}
                            className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                            placeholder="Image title"
                          />
                          <input
                            type="text"
                            value={image.alt || ''}
                            onChange={(e) => updateImage('studentsTrip', 'studyTourHighlights', index, 'alt', e.target.value)}
                            className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                            placeholder="Alt text"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => removeImage('studentsTrip', 'studyTourHighlights', index)}
                              className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                        <ImageUpload
                          value={image.url}
                          onChange={(url) => updateImage('studentsTrip', 'studyTourHighlights', index, 'url', url)}
                          placeholder="Image filename"
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addImage('studentsTrip', 'studyTourHighlights')}
                      className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-crimson hover:text-crimson transition-colors"
                    >
                      <i className="fas fa-plus mr-2"></i>
                      Add Study Tour Photo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tree Plantation Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Environmental Initiatives - Tree Plantation</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Section Title</label>
              <input
                type="text"
                value={formData.treePlantation?.title || ''}
                onChange={(e) => handleNestedChange('treePlantation', 'title', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Description</label>
              <textarea
                value={formData.treePlantation?.description || ''}
                onChange={(e) => handleNestedChange('treePlantation', 'description', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Tree Plantation Images</label>
              <div className="space-y-4">
                {formData.treePlantation?.images?.map((image, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <input
                        type="text"
                        value={image.title || ''}
                        onChange={(e) => updateImage('treePlantation', 'images', index, 'title', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="Image title"
                      />
                      <input
                        type="text"
                        value={image.alt || ''}
                        onChange={(e) => updateImage('treePlantation', 'images', index, 'alt', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="Alt text"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => removeImage('treePlantation', 'images', index)}
                          className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <ImageUpload
                      value={image.url}
                      onChange={(url) => updateImage('treePlantation', 'images', index, 'url', url)}
                      placeholder="Image filename"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addImage('treePlantation', 'images')}
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-crimson hover:text-crimson transition-colors"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Add Tree Plantation Image
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Impact Statistics</label>
              {formData.treePlantation?.impact?.stats?.map((stat, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={stat.label}
                    onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                    className="flex-1 px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="Stat label"
                  />
                  <input
                    type="text"
                    value={stat.value}
                    onChange={(e) => handleStatChange(index, 'value', e.target.value)}
                    className="w-24 px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="Value"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Digital Learning Initiative Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Digital Learning Initiative - Device Distribution</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Section Title</label>
              <input
                type="text"
                value={formData.tabDistribution?.title || ''}
                onChange={(e) => handleNestedChange('tabDistribution', 'title', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Description</label>
              <textarea
                value={formData.tabDistribution?.description || ''}
                onChange={(e) => handleNestedChange('tabDistribution', 'description', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Benefits</label>
              {formData.tabDistribution?.benefits?.items?.map((item, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleListItemChange('tabDistribution', 'benefits', index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                  />
                  <button
                    type="button"
                    onClick={() => removeListItem('tabDistribution', 'benefits', index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addListItem('tabDistribution', 'benefits')}
                className="text-crimson hover:text-red-800 text-sm"
              >
                <i className="fas fa-plus mr-1"></i>
                Add Benefit
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Digital Learning Images</label>
              <div className="space-y-4">
                {formData.tabDistribution?.images?.map((image, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <input
                        type="text"
                        value={image.title || ''}
                        onChange={(e) => updateImage('tabDistribution', 'images', index, 'title', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="Image title"
                      />
                      <input
                        type="text"
                        value={image.alt || ''}
                        onChange={(e) => updateImage('tabDistribution', 'images', index, 'alt', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="Alt text"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => removeImage('tabDistribution', 'images', index)}
                          className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <ImageUpload
                      value={image.url}
                      onChange={(url) => updateImage('tabDistribution', 'images', index, 'url', url)}
                      placeholder="Image filename"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addImage('tabDistribution', 'images')}
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-crimson hover:text-crimson transition-colors"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Add Digital Learning Image
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Yoga Day Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">International Yoga Day Celebration</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Section Title</label>
              <input
                type="text"
                value={formData.yogaDay?.title || ''}
                onChange={(e) => handleNestedChange('yogaDay', 'title', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Description</label>
              <textarea
                value={formData.yogaDay?.description || ''}
                onChange={(e) => handleNestedChange('yogaDay', 'description', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Yoga Day Images</label>
              <div className="space-y-4">
                {formData.yogaDay?.images?.map((image, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <input
                        type="text"
                        value={image.title || ''}
                        onChange={(e) => updateImage('yogaDay', 'images', index, 'title', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="Image title"
                      />
                      <input
                        type="text"
                        value={image.alt || ''}
                        onChange={(e) => updateImage('yogaDay', 'images', index, 'alt', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="Alt text"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => removeImage('yogaDay', 'images', index)}
                          className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <ImageUpload
                      value={image.url}
                      onChange={(url) => updateImage('yogaDay', 'images', index, 'url', url)}
                      placeholder="Image filename"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addImage('yogaDay', 'images')}
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-crimson hover:text-crimson transition-colors"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Add Yoga Day Image
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sports Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Student Activities</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Section Title</label>
              <input
                type="text"
                value={formData.sports?.title || ''}
                onChange={(e) => handleNestedChange('sports', 'title', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Description</label>
              <textarea
                value={formData.sports?.description || ''}
                onChange={(e) => handleNestedChange('sports', 'description', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Sports Activities</label>
              {formData.sports?.activities?.map((activity, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={activity.title}
                    onChange={(e) => handleSportsActivityChange(index, 'title', e.target.value)}
                    className="flex-1 px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="Activity title"
                  />
                  <input
                    type="text"
                    value={activity.icon}
                    onChange={(e) => handleSportsActivityChange(index, 'icon', e.target.value)}
                    className="w-32 px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                    placeholder="Icon class"
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Sports Facilities</label>
              {formData.sports?.facilities?.items?.map((item, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleListItemChange('sports', 'facilities', index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                  />
                  <button
                    type="button"
                    onClick={() => removeListItem('sports', 'facilities', index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addListItem('sports', 'facilities')}
                className="text-crimson hover:text-red-800 text-sm"
              >
                <i className="fas fa-plus mr-1"></i>
                Add Facility
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Recent Achievements</label>
              {formData.sports?.achievements?.items?.map((item, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleListItemChange('sports', 'achievements', index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                  />
                  <button
                    type="button"
                    onClick={() => removeListItem('sports', 'achievements', index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addListItem('sports', 'achievements')}
                className="text-crimson hover:text-red-800 text-sm"
              >
                <i className="fas fa-plus mr-1"></i>
                Add Achievement
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Sports Activity Images</label>
              <div className="space-y-4">
                {formData.sports?.images?.map((image, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <input
                        type="text"
                        value={image.title || ''}
                        onChange={(e) => updateImage('sports', 'images', index, 'title', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="Image title"
                      />
                      <input
                        type="text"
                        value={image.alt || ''}
                        onChange={(e) => updateImage('sports', 'images', index, 'alt', e.target.value)}
                        className="px-3 py-2 border border-border_gray rounded focus:outline-none focus:ring-2 focus:ring-crimson"
                        placeholder="Alt text"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => removeImage('sports', 'images', index)}
                          className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <ImageUpload
                      value={image.url}
                      onChange={(url) => updateImage('sports', 'images', index, 'url', url)}
                      placeholder="Image filename"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addImage('sports', 'images')}
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-crimson hover:text-crimson transition-colors"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Add Sports Activity Image
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">Call to Action Section</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">CTA Title</label>
              <input
                type="text"
                value={formData.ctaSection?.title || ''}
                onChange={(e) => handleNestedChange('ctaSection', 'title', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">CTA Description</label>
              <textarea
                value={formData.ctaSection?.description || ''}
                onChange={(e) => handleNestedChange('ctaSection', 'description', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Facebook Link</label>
                <input
                  type="url"
                  value={formData.ctaSection?.socialLinks?.facebook || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    ctaSection: {
                      ...prev.ctaSection,
                      socialLinks: {
                        ...prev.ctaSection.socialLinks,
                        facebook: e.target.value
                      }
                    }
                  }))}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">Instagram Link</label>
                <input
                  type="url"
                  value={formData.ctaSection?.socialLinks?.instagram || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    ctaSection: {
                      ...prev.ctaSection,
                      socialLinks: {
                        ...prev.ctaSection.socialLinks,
                        instagram: e.target.value
                      }
                    }
                  }))}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">YouTube Link</label>
                <input
                  type="url"
                  value={formData.ctaSection?.socialLinks?.youtube || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    ctaSection: {
                      ...prev.ctaSection,
                      socialLinks: {
                        ...prev.ctaSection.socialLinks,
                        youtube: e.target.value
                      }
                    }
                  }))}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate mb-2">LinkedIn Link</label>
                <input
                  type="url"
                  value={formData.ctaSection?.socialLinks?.linkedin || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    ctaSection: {
                      ...prev.ctaSection,
                      socialLinks: {
                        ...prev.ctaSection.socialLinks,
                        linkedin: e.target.value
                      }
                    }
                  }))}
                  className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                />
              </div>
            </div>
          </div>
        </div>

        {/* SEO Section */}
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6">
          <h2 className="text-xl font-sans font-semibold text-slate mb-4">SEO Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate mb-2">Meta Title</label>
              <input
                type="text"
                value={formData.metaTitle}
                onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate mb-2">Meta Description</label>
              <textarea
                value={formData.metaDescription}
                onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                className="w-full px-4 py-3 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventsPageEdit;